<template>
  <div id="app">
    <!-- Demo User Selector Modal (local development only) -->
    <div v-if="showDemoUserSelector" class="demo-selector-overlay">
      <DemoUserSelector 
        @close="showDemoUserSelector = false"
        @user-selected="handleDemoUserSelected"
      />
    </div>

    <!-- If user is authenticated, show the main app -->
    <template v-if="isAuthenticated && user">
      <AppTopbar />
      <div class="main-container">
        <AppSidebar :user="user" :is-local-only="IS_LOCAL_ONLY_MODE" @submenu-action="handleSubmenuAction" />
        <div class="content-area">
          <div v-if="appLoadError" class="error-placeholder">
            <p>Error preparing application component definition:</p>
            <pre>{{ appLoadError.message }}</pre>
            <pre v-if="appLoadError.stack">{{ appLoadError.stack }}</pre>
          </div>
          <router-view v-else />
        </div>
      </div>
    </template>
    <!-- If not authenticated and not in an auth redirect process, show a login button -->
    <template v-else-if="!authInProgress">
      <div class="login-prompt-container">
        <div class="login-left-column">
          <div class="login-content">
            <div class="logo-container">
              <img :src="rstLogoSrc" alt="Customer Logo" class="login-logo" />
            </div>
            <!-- Core CORE_AUTH_MODE=local: username/password form instead of the Cognito Hosted UI -->
            <LocalLogin v-if="authMode === 'local'" :notice="loginNotice" @signed-in="enterLocalSession" />
            <button v-else @click="redirectToCognitoHostedUI" class="login-button">
              Proceed to secure sign in
            </button>
          </div>
        </div>
        <div class="login-right-column">
          <img :src="archGraphicSrc" alt="Arch Graphic" class="arch-graphic" />
        </div>
      </div>
    </template>
    <!-- Show a loading indicator during auth checks -->
    <div v-if="authInProgress" class="loading-placeholder">
      <p>Loading authentication status...</p>
    </div>

    <!-- Global Notification Component -->
    <AppNotification />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, onErrorCaptured, watch } from 'vue';
import { AUTH_REQUIRED_EVENT } from './utils/sessionExpiry';
import { useShellStore } from './stores/shellStore';
import { Auth, Hub } from 'aws-amplify';
import type { HubCapsule } from '@aws-amplify/core'; // For Hub payload type
import AppTopbar from './components/shell/AppTopbar.vue';
import AppSidebar from './components/shell/AppSidebar.vue';
import DemoUserSelector from './components/DemoUserSelector.vue';
import AppNotification from './components/AppNotification.vue';
import LocalLogin from './components/auth/LocalLogin.vue';
import { useAppStore } from './stores/appStore';
import { usePermissionsStore } from './stores/permissionsStore';
import { router, rootRedirectTarget } from './router';
import { resolveModules, getDefaultModuleId } from './config/moduleRegistry';
import {
  consumeSsoParamFromUrl, takePendingSsoParam, resolveSsoHandoff, getSsoToken, getSsoUser, clearSsoSession,
} from './utils/ssoSession';
import {
  getBootAuthMode, fetchAuthMode, getLocalToken, getLocalUser, clearLocalSession, type AuthMode, type LocalUser,
} from './utils/localSession';
import archGraphicSrc from '@/assets/grey_logo.webp';

// Customer logo resolved from public dir based on VITE_CUSTOMER_ID env var
const customerId = import.meta.env.VITE_CUSTOMER_ID || 'core';
const rstLogoSrc = `/logos/${customerId}/customer_logo.webp`;

// Update favicon to match customer
const favicon = document.getElementById('favicon') as HTMLLinkElement | null;
if (favicon) favicon.href = rstLogoSrc;
// Default Amplify UI styles are not needed here if not using Authenticator component directly
// import '@aws-amplify/ui-vue/styles.css'; 

// Helper to determine if we're in "local only" mode (dev server AND no Cognito pool ID)
const IS_LOCAL_ONLY_MODE = import.meta.env.DEV && !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const appStore = useAppStore();
const permissionsStore = usePermissionsStore();
const shellStore = useShellStore();
const appLoadError = ref<Error | null>(null);

const isAuthenticated = ref(false);
const user = ref<any | null>(null); // To store user attributes
const authInProgress = ref(true); // To handle initial auth check and Hub events
// Shell sidebar badges: poll the Operations summary only while signed in.
watch(isAuthenticated, (on) => { if (on) shellStore.startPolling(); else shellStore.stopPolling(); }, { immediate: true });
const showDemoUserSelector = ref(false);
// 'local' only when Core runs CORE_AUTH_MODE=local (GET /api/auth/mode); otherwise today's Cognito flow.
const authMode = ref<AuthMode>('cognito');
const loginNotice = ref<string | null>(null);

const enterLocalSession = async (localUser: LocalUser) => {
  loginNotice.value = null;
  isAuthenticated.value = true;
  user.value = localUser;
  permissionsStore.reset();
  await permissionsStore.fetchUserPermissions(true);
  authInProgress.value = false;
  rerunRootGuardIfNeeded();
};

// Local sessions end locally: clear the token and show the login form again (never a blank page).
const signOutLocal = (notice: string | null = null) => {
  clearLocalSession();
  isAuthenticated.value = false;
  user.value = null;
  permissionsStore.reset();
  loginNotice.value = notice;
  authInProgress.value = false;
};

// After permissions are loaded, re-resolve the landing module. The router
// guard's initial run happened before the session existed (URL still `/`), or
// under a session that has since been replaced (an SSO handoff swapping a full
// session for a scoped one leaves us on a module the new scope does not allow).
// Resolve the target directly (mirrors the guard's logic) and push to that
// concrete path — re-entering the guard via replace('/') has triggered
// Pinia/devtools wrapper issues in production builds.
const rerunRootGuardIfNeeded = () => {
  const current = router.currentRoute.value;
  const target = rootRedirectTarget(
    current.path,
    current.meta?.moduleId,
    resolveModules(),
    getDefaultModuleId(),
    (id) => permissionsStore.hasPermission(id),
  );
  if (target && target !== current.path) {
    router.replace(target).catch(() => { /* swallow */ });
  }
};

const redirectToCognitoHostedUI = () => {
  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, Cognito redirect is skipped.');
    // Optionally, you could simulate an error or do nothing
    // To test the login UI again if needed in local_only_mode, you could set:
    // isAuthenticated.value = false;
    // user.value = null;
    // authInProgress.value = false; 
    return;
  }
  authInProgress.value = true; // Show loading indicator during redirect
  Auth.federatedSignIn(); // This redirects to the Cognito Hosted UI
};

const handleCognitoSignOut = async () => {
  if (user.value?.local) { signOutLocal(); return; }
  // External-SSO sessions sign out locally; there is no Cognito session to end.
  if (user.value?.sso) {
    clearSsoSession();
    isAuthenticated.value = false;
    user.value = null;
    permissionsStore.reset();
    authInProgress.value = false;
    return;
  }
  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, simulating sign out.');
    isAuthenticated.value = false;
    user.value = null;
    permissionsStore.reset(); // Clear permissions on sign out
    authInProgress.value = false;
    return;
  }
  authInProgress.value = true; // Show loading indicator
  try {
    await Auth.signOut();
    // isAuthenticated, user, and permissions will be updated by the Hub listener
  } catch (error) {
    console.error('Error signing out with Cognito: ', error);
    authInProgress.value = false; // Hide loading if sign out fails
  }
};

// Session expiry: utils/sessionExpiry dispatches this (at most once per 30 s) when Core answers 401 to an
// /api call that carried no token — the browser session is gone. Cognito users go back to the Hosted UI;
// external-SSO sessions are signed out locally so the login screen shows.
const onAuthRequired = () => {
  if (authMode.value === 'local') {
    // The rejected local token is already cleared by the fetch watch (createApp); show the form.
    if (isAuthenticated.value) signOutLocal('Your session has ended. Please sign in again.');
    return;
  }
  if (IS_LOCAL_ONLY_MODE || !isAuthenticated.value || authInProgress.value) return;
  console.warn('[App.vue] API rejected a token-less call (401): session lapsed, re-authenticating');
  if (user.value?.sso) { void handleCognitoSignOut(); return; }
  redirectToCognitoHostedUI();
};
window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
onUnmounted(() => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired));

// Central dispatcher for all account actions emitted by the sidebar's account menu
const handleSubmenuAction = (action: string) => {
  switch (action) {
    case 'sign-out':
      handleCognitoSignOut();
      break;
    case 'switch-user':
      showDemoUserSelector.value = true;
      break;
    default:
      console.warn(`[App.vue] Unhandled submenu action: "${action}"`);
      break;
  }
};

// Handler for demo user selection
const handleDemoUserSelected = (selectedUser: any) => {
  if (selectedUser) {
    user.value = {
      username: selectedUser.username,
      attributes: {
        name: selectedUser.name,
        email: selectedUser.email,
        ...selectedUser.attributes
      },
    };
  }
  showDemoUserSelector.value = false;
};

// Function to fetch and apply demo user context
const fetchDemoUserContext = async () => {
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const response = await fetch(`${baseUrl}/api/demo-auth/current`);
    
    if (response.ok) {
      const data = await response.json();
      if (data.user) {
        return {
          username: data.user.username,
          attributes: {
            name: data.user.name,
            email: data.user.email,
            ...data.user.attributes
          },
          groups: data.user.groups
        };
      }
    }
  } catch (error) {
    console.error('Error fetching demo user context:', error);
  }
  return null;
};

onMounted(async () => {
  // --- External SSO handoff (e.g. Odoo menu → ?odoo_sso=<one-time token>) ---
  // Runs before every other auth mode, but is inert unless the URL param or a
  // stored SSO session exists — the Cognito and demo flows below are untouched.
  // createApp() captured the param before the router could redirect it away.
  const ssoParam = takePendingSsoParam() ?? consumeSsoParamFromUrl();
  if (ssoParam) {
    authInProgress.value = true;
    // Clears any existing SSO session first, then stores the new one on success:
    // a failed exchange falls through to the login screen, never a stale session.
    await resolveSsoHandoff(ssoParam);
  }
  if (getSsoToken()) {
    const ssoUser = getSsoUser();
    if (ssoUser) {
      console.log('[App.vue] Using external SSO session for', ssoUser.username);
      isAuthenticated.value = true;
      user.value = ssoUser;
      permissionsStore.reset();
      await permissionsStore.fetchUserPermissions();
      authInProgress.value = false;
      rerunRootGuardIfNeeded();
      return; // skip Amplify/demo wiring entirely for SSO sessions
    }
  }
  if (ssoParam) {
    // Exchange failed — fall through to the normal login screen.
    authInProgress.value = false;
  }

  // --- Local password auth (Core CORE_AUTH_MODE=local) ---
  // createApp resolved the mode before mount; 'cognito' (incl. any fetch failure) skips this block.
  const mode = getBootAuthMode() ?? await fetchAuthMode();
  if (mode === 'local') {
    authMode.value = 'local';
    const localUser = getLocalToken() ? getLocalUser() : null;
    if (localUser) {
      await enterLocalSession(localUser);
    } else {
      signOutLocal();
    }
    return; // no Amplify/demo wiring in local mode
  }

  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, checking for demo user context.');
    
    // Reset permissions to force a fresh fetch
    permissionsStore.reset();
    
    // Try to get the selected demo user
    const demoUser = await fetchDemoUserContext();
    
    if (demoUser) {
      console.log('[App.vue] Using demo user:', demoUser.username);
      isAuthenticated.value = true;
      user.value = demoUser;
      authInProgress.value = false;
      
      // Fetch permissions for the demo user
      await permissionsStore.fetchUserPermissions();
      rerunRootGuardIfNeeded();
    } else {
      console.log('[App.vue] No demo user selected, showing selector.');
      // No user selected, show the selector
      isAuthenticated.value = false;
      showDemoUserSelector.value = true;
      authInProgress.value = false;
    }
    
    // DO NOT set up Hub listener or call currentAuthenticatedUser in this mode
    return; // IMPORTANT: Skip the rest of onMounted for local_only
  }

  // --- Full Cognito Auth Logic (for dev-with-cognito or production) ---
  // Listener for authentication events from Amplify Hub
  const hubListenerCancel = Hub.listen('auth', async ({ payload }: HubCapsule) => {
    const { event, data } = payload;
    switch (event) {
      case 'signIn': // This event fires after successful sign in via Hosted UI
      case 'cognitoHostedUI':
        console.log('User signed in via Hosted UI:', data);
        isAuthenticated.value = true;
        user.value = data; // data here is the CognitoUser object
        // Fetch user permissions after sign in
        await permissionsStore.fetchUserPermissions();
        authInProgress.value = false;
        rerunRootGuardIfNeeded();
        // Potentially redirect to a specific page if on a callback route
        // if (window.location.pathname === '/callback') { router.push('/'); }
        break;
      case 'signOut':
        console.log('User signed out');
        isAuthenticated.value = false;
        user.value = null;
        permissionsStore.reset(); // Clear permissions on sign out
        authInProgress.value = false;
        break;
      case 'signIn_failure':
      case 'cognitoHostedUI_failure':
        console.error('Sign in failure with Hosted UI', data);
        isAuthenticated.value = false;
        user.value = null;
        authInProgress.value = false;
        // Handle the error, maybe show a message to the user
        break;
      default:
        // console.log('Hub Auth event:', event, data);
        break;
    }
  });

  // Check current authenticated user on page load
  Auth.currentAuthenticatedUser()
    .then(async currentUser => {
      console.log('User currently authenticated:', currentUser);
      isAuthenticated.value = true;
      user.value = currentUser; // currentUser is the CognitoUser object
      // Fetch user permissions for already authenticated user
      await permissionsStore.fetchUserPermissions();
      authInProgress.value = false;
      rerunRootGuardIfNeeded();
    })
    .catch(err => {
      console.log('No user currently authenticated:', err);
      isAuthenticated.value = false;
      user.value = null;
      authInProgress.value = false;
    });
  
  // Clean up the Hub listener when the component is unmounted
  onUnmounted(() => {
    // Conditionally unsubscribe only if the listener was set up
    if (!IS_LOCAL_ONLY_MODE && hubListenerCancel) {
       hubListenerCancel(); // Call the unsubscribe function
    }
  });
});

onErrorCaptured((err, _instance, info) => {
  console.error('Runtime error captured in App.vue:', err, info);
  if (!appLoadError.value) {
    appLoadError.value = err;
  }
  return false; // Prevent further propagation if handled
});

watch(() => appStore.currentAppId, (newAppId, oldAppId) => {
  if (newAppId !== oldAppId) {
    appLoadError.value = null; // Clear component load error when app changes
  }
});

// Computed property for dynamic main container height
</script>

<style>
/* Global styles remain the same */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  /* Shell dimensions live in assets/design-tokens.css (--shell-topbar-h, --shell-sidebar-w). */
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden; /* Keep this if your app layout relies on it */
}

#app {
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden; /* Keep this */
  display: flex;
  flex-direction: column;
}

/* Demo user selector overlay */
.demo-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

/* Styles for the login prompt */
.login-prompt-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: #f0f2f5;
  flex-direction: row;
}

.login-left-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background-color: white;
}

.login-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 350px;
}

.logo-container {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.login-logo {
  height: 50px;
  margin-right: 10px;
}

.logo-text {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.login-button {
  background-color: #5E6977;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
}

.login-button:hover {
  background-color: #4A5460;
}

.login-right-column {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.arch-graphic {
  max-width: 250px;
  max-height: 250px;
  object-fit: contain;
}

.main-container {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  height: calc(100vh - var(--shell-topbar-h));
  position: relative;
}

.content-area {
  flex-grow: 1;
  min-width: 0;
  overflow: auto;
  padding: 0;
  background: var(--ground);
}

.loading-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.2em;
  color: #777;
}

.error-placeholder {
  padding: 20px;
  color: red;
  background-color: #ffe0e0;
  border: 1px solid red;
  border-radius: 5px;
  height: 100%;
  overflow: auto;
}
.error-placeholder pre {
  white-space: pre-wrap;
  word-break: break-all;
  margin-top: 10px;
  font-size: 0.9em;
}
</style> 